import * as React from "react";
import { useContext } from "react";
import { useAppTabList } from "@1js/acui-tablist";
import { dataUniqueId, getInputMethodFromEvent } from "@1js/acui-common";
import { useEventCallback, mergeCallbacks } from "@fluentui/react-utilities";
import { mergeClasses } from "@fluentui/react-components";
import type { TabListProps, TabListState } from "./TabList.types";
import type { AppTabListOverflowMenuProps } from "@1js/acui-tablist";
import { useCommonArrowNavGroup } from "@1js/acui-focus";
import { AppContext } from "@1js/acui-context";
import { useOnKeyDown } from "./useOnKeyDown";
import { InputMethod } from "@1js/acui-common";
import type { PivotHeaderExecutePayload } from "@1js/acui-pivot";

const noOpFileMenuOpenId = "noOpFileMenuOpenId";

export const useTablist = (
  props: TabListProps,
  ref: React.ForwardedRef<HTMLElement>
): TabListState => {
  const {
    onOverflowButtonExecute,
    onTabHeaderClick,
    overflowMenuLauncherLabel,
    overflowTabHeaderMenuLauncherIcon,
    overflowTabHeaders,
    topLevelTabHeaders,
    useNormalStyleForContextualTabs,
    tabListOverflowMenu,
    hideSelectedTabIndicators,
    keytipProps,
    selectedTabId,
    focusTabListRef,
    preventMenuExecution,
  } = props;

  const [localSelectedTabId, setSelectedTabId] = React.useState(selectedTabId);

  const [focusOnOverflow, setFocusOnOverflow] = React.useState(false);

  // Ensure the selectedTab from props is set as the localSelectedTab
  // in the case where a contextual tab is added/removed and set elsewhere
  React.useEffect(() => {
    setSelectedTabId(selectedTabId);
  }, [selectedTabId, setSelectedTabId]);

  const onTabSelect = React.useCallback(
    (
      tabId: string,
      ev?:
        | React.MouseEvent<HTMLDivElement, MouseEvent>
        | React.KeyboardEvent<HTMLDivElement>
    ) => {
      onTabHeaderClick(tabId, {
        inputMethod: getInputMethodFromEvent(ev),
      });
      setSelectedTabId(tabId);
    },
    [onTabHeaderClick, setSelectedTabId]
  );

  const onTabSelectKeytip = React.useCallback(
    (tabId: string) => {
      onTabHeaderClick(tabId, {
        inputMethod: InputMethod.Keyboard,
      });
      setSelectedTabId(tabId);
    },
    [onTabHeaderClick, setSelectedTabId]
  );

  React.useImperativeHandle(
    focusTabListRef,
    () => ({
      focus: () => {
        setFocusOnOverflow(false);
        props?.onFocus?.();
      },
    }),
    [setFocusOnOverflow, props]
  );

  const onClick = React.useCallback(
    (tabId: string, data?: PivotHeaderExecutePayload) => {
      const payload: PivotHeaderExecutePayload = {
        ...data,
        inputMethod: data?.inputMethod ?? InputMethod.Mouse,
      };

      onTabHeaderClick(tabId, payload);

      // We want to set the activeTab only after the ribbon is fully loaded
      // meanwhile we want click to register to show PRI Toast but we do not
      // want the tab to highlight on click.
      if (!preventMenuExecution) {
        setSelectedTabId(tabId);
      }
    },
    [onTabHeaderClick, setSelectedTabId, preventMenuExecution]
  );

  const tabs = topLevelTabHeaders.map((header) => ({
    ...header,
    "data-unique-id": dataUniqueId(header),
    "data-automation-type": props.automationType,
    key: header.id,
    value: header.id,
    onTabHeaderExecute: onClick,
    useNormalStyleForContextualTabs,
    children: header.title,
    hideSelectedIndicator: hideSelectedTabIndicators,
    title: header.title || "",
  }));

  // Add in the keytip information for the overflow tabs
  const overflowTabs = overflowTabHeaders?.map((header) => ({
    ...header,
    keytipProps: {
      ...header.keytipProps,
      content: header.keytipProps?.content || "",
      hasDynamicChildren: true,
      keySequences: header.keytipProps?.keySequences || [],
      overflowSetSequence: ["00"],
      offset: { x: 10, y: 10 },
    },
  }));

  const context = useContext(AppContext);

  const memoizedMenuListWrapper = React.useMemo(() => {
    return { className: mergeClasses(context.responsiveClassName) };
  }, [context.responsiveClassName]);

  const tabListOverflowMenuState: AppTabListOverflowMenuProps = {
    ariaLabel: overflowMenuLauncherLabel,
    ...(tabListOverflowMenu as AppTabListOverflowMenuProps),
    overflowTabHeaderMenuLauncherIcon,
    topItems: topLevelTabHeaders,
    overflowItems: overflowTabs || [],
    keytipProps,
    onOverflowButtonExecute,
    menuListWrapper: memoizedMenuListWrapper,
    onOverflowTabSelect: onTabSelectKeytip,
  };

  tabListOverflowMenuState.onMenuButtonClick = useEventCallback(
    mergeCallbacks(tabListOverflowMenuState.onMenuButtonClick, () =>
      onOverflowButtonExecute?.({ id: noOpFileMenuOpenId })
    )
  );
  tabListOverflowMenuState.onMenuItemClick = useEventCallback(
    mergeCallbacks(tabListOverflowMenuState.onMenuItemClick, (event, id) =>
      onTabSelect(id, event)
    )
  );

  // Controls the arrow nav for the tablist and ensures
  // the tab event key is not handled so the ribbon container
  // can handle the tab loop
  const focusAttributes = useCommonArrowNavGroup(true /** disableTab */);

  const tabListOnKeyDown = useOnKeyDown(
    props,
    selectedTabId,
    setSelectedTabId,
    setFocusOnOverflow,
    onTabSelect,
    focusOnOverflow
  );

  const tabListState = useAppTabList(
    {
      ...props,
      id: "tablist",
      tabIndex: -1,
      appearance: "subtle",
      onTabSelect: (_event, data) => onTabSelect(data.value as string),
      onTabHeaderClick: onClick,
      selectedTabId: localSelectedTabId,
      onKeyDown: tabListOnKeyDown,
      ...focusAttributes,
    },
    ref
  );

  return {
    ...props,
    ...tabListState,
    tabs,
    tabListOverflowMenu: tabListOverflowMenuState,
  } as TabListState;
};
